import { PREVIEW_NS, type PreviewElement, type PreviewElementsMessage } from "@innoclouds/ui";

const MAX_TEXT = 80;

const MAX_VALUE = 160;

const ALWAYS = "";

const BOX: Record<string, string> = {
	display: ALWAYS,
	position: "static",
	"z-index": "auto",
	padding: "0px",
	margin: "0px",
	overflow: "visible",
	"background-color": "rgba(0, 0, 0, 0)",
	"background-image": "none",
	"border-radius": "0px",
	"box-shadow": "none",
	opacity: "1",
	transform: "none",
	"object-fit": "fill",
};

const BORDER: Record<string, string> = { "border-width": ALWAYS, "border-style": ALWAYS, "border-color": ALWAYS };

const FLEX: Record<string, string> = {
	"flex-direction": ALWAYS,
	"flex-wrap": "nowrap",
	"justify-content": "normal",
	"align-items": "normal",
	gap: "normal",
};

const GRID: Record<string, string> = {
	"grid-template-columns": "none",
	"grid-template-rows": "none",
	"justify-content": "normal",
	"align-items": "normal",
	gap: "normal",
};

const TEXT: Record<string, string> = {
	"font-family": ALWAYS,
	"font-size": ALWAYS,
	"font-weight": ALWAYS,
	"line-height": ALWAYS,
	color: ALWAYS,
	"letter-spacing": "normal",
	"text-align": "start",
	"text-transform": "none",
};

const SAMPLES = [
	[0.5, 0.5],
	[0.25, 0.25],
	[0.75, 0.25],
	[0.25, 0.75],
	[0.75, 0.75],
] as const;

const isZero = (value: string) => value.split(/\s+/).every((part) => part === "0px");

const clipped = (value: string) => (value.length > MAX_VALUE ? `${value.slice(0, MAX_VALUE)}…` : value);

const pick = (target: Record<string, string>, style: CSSStyleDeclaration, properties: Record<string, string>) => {
	for (const [property, initial] of Object.entries(properties)) {
		const value = style.getPropertyValue(property);
		if (!value || value === initial || (initial === "0px" && isZero(value))) continue;
		target[property] = clipped(value);
	}
};

const inViewport = (rect: DOMRect) =>
	rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;

// elementFromPoint не видит элементы с pointer-events: none — на время проверки элемент делаем «кликабельным»
const asHittable = <T>(el: HTMLElement, style: CSSStyleDeclaration, run: () => T) => {
	if (style.pointerEvents !== "none") return run();
	const value = el.style.getPropertyValue("pointer-events");
	const priority = el.style.getPropertyPriority("pointer-events");
	el.style.setProperty("pointer-events", "auto", "important");
	try {
		return run();
	} finally {
		if (value) el.style.setProperty("pointer-events", value, priority);
		else el.style.removeProperty("pointer-events");
	}
};

const isCovered = (el: HTMLElement, rect: DOMRect, style: CSSStyleDeclaration) => {
	const left = Math.max(rect.left, 0);
	const top = Math.max(rect.top, 0);
	const width = Math.min(rect.right, window.innerWidth) - left;
	const height = Math.min(rect.bottom, window.innerHeight) - top;
	return asHittable(el, style, () =>
		SAMPLES.every(([x, y]) => {
			const hit = document.elementFromPoint(left + width * x, top + height * y);
			return hit !== null && !el.contains(hit);
		}),
	);
};

const ownTexts = (el: HTMLElement) => {
	const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
	const texts: Text[] = [];
	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		const parent = node.parentElement;
		if (node.textContent?.trim() && parent?.closest("[data-el]") === el && parent.checkVisibility()) texts.push(node as Text);
	}
	return texts;
};

const styleOf = (el: HTMLElement, computed: CSSStyleDeclaration, texts: Text[]) => {
	const style: Record<string, string> = {};
	pick(style, computed, BOX);
	if (!isZero(computed.getPropertyValue("border-width"))) pick(style, computed, BORDER);
	if (computed.display.endsWith("flex")) pick(style, computed, FLEX);
	if (computed.display.endsWith("grid")) pick(style, computed, GRID);
	const textHost = texts[0]?.parentElement;
	if (textHost) pick(style, textHost === el ? computed : getComputedStyle(textHost), TEXT);
	return style;
};

const parentOf = (el: HTMLElement, indexes: Map<HTMLElement, number>) => {
	let node = el.parentElement?.closest<HTMLElement>("[data-el]");
	while (node && !indexes.has(node)) node = node.parentElement?.closest<HTMLElement>("[data-el]");
	return node ? (indexes.get(node) ?? null) : null;
};

const describeElement = (el: HTMLElement, rect: DOMRect, parent: number | null): PreviewElement => {
	const computed = getComputedStyle(el);
	const texts = ownTexts(el);
	const text = texts
		.map((node) => node.textContent)
		.join(" ")
		.replace(/\s+/g, " ")
		.trim()
		.slice(0, MAX_TEXT);
	return {
		screen: el.dataset.screen ?? null,
		name: el.dataset.el ?? "",
		parent,
		tag: el.tagName.toLowerCase(),
		rect: { x: Math.round(rect.x), y: Math.round(rect.y), width: Math.round(rect.width), height: Math.round(rect.height) },
		...(isCovered(el, rect, computed) && { covered: true }),
		...(text && { text }),
		style: styleOf(el, computed, texts),
	};
};

export const describe = (id: number): PreviewElementsMessage => {
	const indexes = new Map<HTMLElement, number>();
	const elements: PreviewElement[] = [];
	for (const el of document.querySelectorAll<HTMLElement>("[data-el]")) {
		const rect = el.getBoundingClientRect();
		if (!inViewport(rect) || !el.checkVisibility({ opacityProperty: true, visibilityProperty: true })) continue;
		elements.push(describeElement(el, rect, parentOf(el, indexes)));
		indexes.set(el, elements.length - 1);
	}
	return { ns: PREVIEW_NS, type: "elements", id, viewport: { width: window.innerWidth, height: window.innerHeight }, elements };
};
